<template>
  <ZenniZnFilterFilterCategory
    v-if="facet"
    title="Gender"
    :count="selectedProxy.length"
  >
    <ul class="flex flex-col">
      <ZenniZnListItem
        v-for="{ value, productCount, label } in computedFacetValues"
        :key="value"
        :aria-label="label"
        size="lg"
        @click="toggleSelected(value)"
        @keypress.enter.prevent="toggleSelected(value)"
        @keypress.space.prevent="toggleSelected(value)"
      >
        <template #prefix>
          <ZnCheckbox
            :model-value="selectedProxy.includes(value)"
            class="pointer-events-none flex items-center p-1"
            :aria-label="label"
            :aria-labelledby="title"
            :aria-checked="selectedProxy.includes(value)"
            role="checkbox"
            tabindex="0"
          />
        </template>
        <div class="flex gap-2">
          <span class="pl-1">{{ label }}</span>
          <ZenniZnCounter v-if="productCount">{{
            productCount
          }}</ZenniZnCounter>
        </div>
      </ZenniZnListItem>
      <li
        v-if="!showAll && facet.values.length > itemsToShow"
        class="mx-auto cursor-pointer font-semibold"
      >
        <a
          class="text-teal-primary"
          @click="showAll = true"
          tabindex="0"
          @keypress.enter="showAll = true"
          @keypress.space="showAll = true"
        >
          Show more +
        </a>
      </li>
    </ul>
  </ZenniZnFilterFilterCategory>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { maxBy, sumBy } from 'lodash-es'
import type {
  CheckboxItemProps,
  CheckboxItemEmits,
} from '~/components/Zenni/Plp/filter-types'
import {
  KIDS_GENDER_GROUP_STRING,
  MEN_GENDER_GROUP_STRING,
  WOMEN_GENDER_GROUP_STRING,
} from '~/data/categories'

const emit = defineEmits<CheckboxItemEmits>()
const props = defineProps<CheckboxItemProps>()

const itemsToShow = 8

const showAll = ref(false)
const facetToShow = computed(() => {
  if (showAll.value) {
    return props.facet
  }
  return {
    ...props.facet,
    values: props.facet?.values.slice(0, itemsToShow),
  }
})

const toggleSelected = (value: string) => {
  const index = selectedProxy.value.indexOf(value)
  if (index === -1) {
    selectedProxy.value.push(value)
  } else {
    selectedProxy.value.splice(index, 1)
  }
  emit('update:selected', selectedProxy.value)
}

const selectedProxy = computed(() => props.selected)

const transformGroupGenderToArray = (group: string) =>
  group.replace(/\[|\]/g, '').split(',')

const computedFacetValues = computed(() => {
  const WomenGenders = transformGroupGenderToArray(WOMEN_GENDER_GROUP_STRING)
  const MenGenders = transformGroupGenderToArray(MEN_GENDER_GROUP_STRING)
  const KidsGenders = transformGroupGenderToArray(KIDS_GENDER_GROUP_STRING)

  const withWomen = facetToShow.value?.values?.filter((facet) =>
    WomenGenders.includes(facet.value),
  )

  const withMen = facetToShow.value?.values?.filter((facet) =>
    MenGenders.includes(facet.value),
  )

  // const withUnisex = facetToShow.value?.values?.filter(
  //   (facet) => facet.value.toLowerCase() === 'unisex',
  // )

  const withKids = facetToShow.value?.values?.filter((facet) =>
    KidsGenders.includes(facet.value),
  )

  const women = {
    label: 'Women',
    value: WOMEN_GENDER_GROUP_STRING,
    productCount: sumBy(withWomen, 'productCount'),
  }

  const men = {
    label: 'Men',
    value: MEN_GENDER_GROUP_STRING,
    productCount: sumBy(withMen, 'productCount'),
  }

  // const unisex = {
  //   label: 'Unisex',
  //   value: 'unisex',
  //   productCount:
  //     maxBy(withUnisex, (item: any) => item.productCount)?.productCount ?? 0,
  // }

  const kids = {
    label: 'Kids',
    value: KIDS_GENDER_GROUP_STRING,
    productCount: sumBy(withKids, 'productCount'),
  }

  return [women, men, kids].filter((x) => x.productCount > 0)
})
</script>
